/**
 * Permessi d'area del Magazzino (inventario, trasferimenti, procurement).
 *
 * Unica fonte per:
 *   - il guard globale BE (`route-permission.middleware.ts`: voci `/pos/inventory`,
 *     `/pos/transfers`, `/inventory`, `/catalog/serials`, `/pos/lookups`);
 *   - la visibilità FE della voce "Magazzino" e del route guard del LogisticsHub.
 *
 * Regola: la LETTURA di un'area è concessa a chi possiede uno qualsiasi dei
 * permessi d'azione dell'area (oltre a `pos.amministrazione`); la SCRITTURA
 * resta vincolata ai permessi d'azione già applicati dalle guardie route-local
 * (`checkPermissionStrict` / `checkAnyPermission`). Il guard globale è un
 * recinto di area, non sostituisce il controllo fine per-rotta.
 */
import { TRANSFER_PHASE_PERMISSIONS, TRANSFER_FLOW_PERMISSIONS, TRANSFER_AUDIT_VIEW_PERMISSION } from './transfers';

export const POS_ADMINISTRATION_PERMISSION = 'pos.amministrazione';
export const POS_CATALOG_PERMISSION = 'pos.catalogo';

/** Permessi d'azione delle sessioni di inventario fisico (route-local strict). */
export const INVENTORY_ACTION_PERMISSIONS: readonly string[] = [
  'inventory.create',
  'inventory.count',
  'inventory.close',
  'inventory.reopen',
  'inventory.bypass_lock',
  // Decisione sulle righe oltre soglia di una sessione chiusa.
  'adjustment.threshold_override',
];

/** Lettura area Inventario: amministrazione POS o un qualsiasi permesso d'azione. */
export const INVENTORY_AREA_READ_PERMISSIONS: readonly string[] = [
  POS_ADMINISTRATION_PERMISSION,
  ...INVENTORY_ACTION_PERMISSIONS,
];

/** Permessi d'azione dei trasferimenti: fasi + flussi (route-local strict/any). */
export const TRANSFER_ACTION_PERMISSIONS: readonly string[] = [
  ...Object.values(TRANSFER_PHASE_PERMISSIONS),
  ...Object.values(TRANSFER_FLOW_PERMISSIONS),
];

/** Lettura area Trasferimenti: amministrazione POS, fasi o audit. */
export const TRANSFER_AREA_READ_PERMISSIONS: readonly string[] = [
  POS_ADMINISTRATION_PERMISSION,
  ...Object.values(TRANSFER_PHASE_PERMISSIONS),
  TRANSFER_AUDIT_VIEW_PERMISSION,
];

/** Permessi d'azione del procurement (ordini / carico merce). */
export const PROCUREMENT_ACTION_PERMISSIONS: readonly string[] = [
  'purchase_order.create',
  'purchase_order.approve',
  'goods_receipt.create',
  'goods_receipt.from_ocr',
];

/**
 * Lettura della vista Inventario read-only (`/api/inventory/*`) e dei seriali
 * di catalogo (`/api/catalog/serials/*`): inventario, catalogo o amministrazione.
 */
export const WAREHOUSE_STOCK_READ_PERMISSIONS: readonly string[] = [
  POS_ADMINISTRATION_PERMISSION,
  POS_CATALOG_PERMISSION,
  ...INVENTORY_ACTION_PERMISSIONS,
];

/**
 * Scrittura seriali/lotti da `/api/catalog/serials` e `/api/inventory/lots`:
 * catalogo (anagrafica unità) o carico merce (shortcut "Crea lotto"/unità al carico).
 */
export const WAREHOUSE_STOCK_WRITE_PERMISSIONS: readonly string[] = [
  POS_CATALOG_PERMISSION,
  'goods_receipt.create',
];

/**
 * Ordini fornitore: lettura/scrittura dell'area a chi crea o approva ordini
 * (le mutation restano gated per-rotta: create/approve strict nel router).
 */
export const PROCUREMENT_ORDER_PERMISSIONS: readonly string[] = [
  POS_ADMINISTRATION_PERMISSION,
  'purchase_order.create',
  'purchase_order.approve',
];

function uniq(codes: readonly string[]): readonly string[] {
  return Array.from(new Set(codes));
}

/**
 * Area Magazzino (hub Logistica): chi possiede uno di questi permessi vede la
 * voce di menu, apre l'hub e può leggere il lookup ragioni sociali/sedi
 * necessario a ordini e anagrafica magazzini.
 */
export const WAREHOUSE_AREA_PERMISSIONS: readonly string[] = uniq([
  POS_ADMINISTRATION_PERMISSION,
  ...INVENTORY_ACTION_PERMISSIONS,
  ...TRANSFER_AREA_READ_PERMISSIONS,
  ...PROCUREMENT_ACTION_PERMISSIONS,
]);

/**
 * DTO minimi del lookup read-only `/api/pos/lookups/*`: identità e recapiti
 * fiscali pubblici della RS (quelli stampati in testata ordine/DDT). MAI
 * configurazione provider fiscale, logo, governance o conteggi.
 */
export interface CompanyLookupItem {
  id: string;
  legal_name: string | null;
  vat_number: string | null;
  tax_code: string | null;
  status: string;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  state: string | null;
  sdi_code: string | null;
  pec_email: string | null;
}

export interface StoreLookupItem {
  id: string;
  name: string;
  company_id: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
}
